import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { FormTooltip } from "@/components/ui/form-description";
import { cn } from "@/lib/utils";
import { toCountryFieldValue } from "@/lib/address-search-types";

interface AddressFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  description?: string;
  descriptionAsTooltip?: boolean;
  label: string;
  name: TName;
  required?: boolean;
  requiredLabel?: string;
  country?: "US" | "CA" | "GB" | "AU" | "other";
  stateOptions?: { value: string; label: string }[];
  showAddressLine2?: boolean;
  addressLine1Label?: string;
  addressLine1Placeholder?: string;
  addressLine2Label?: string;
  addressLine2Placeholder?: string;
  cityLabel?: string;
  cityPlaceholder?: string;
  stateLabel?: string;
  statePlaceholder?: string;
  postalCodeLabel?: string;
  postalCodePlaceholder?: string;
  /**
   * Options for the country select. This component does not resync
   * `stateOptions`/labels/placeholders when the user picks a different
   * country here — those stay tied to the `country` prop. If you supply a
   * list spanning multiple countries, keep the dependent props in sync
   * yourself (e.g. by watching this field's value).
   */
  countryOptions?: { value: string; label: string }[];
  countryLabel?: string;
  countryPlaceholder?: string;
}

export function AddressField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  description,
  descriptionAsTooltip = false,
  label,
  name,
  required,
  requiredLabel = "Required",
  country = "US",
  stateOptions,
  showAddressLine2 = true,
  addressLine1Label = "Address Line 1",
  addressLine2Label = "Address Line 2",
  cityLabel = "City",
  stateLabel = getDefaultStateLabel(country),
  postalCodeLabel = getDefaultPostalCodeLabel(country),
  addressLine1Placeholder = getDefaultAddressLine1Placeholder(country),
  addressLine2Placeholder = getDefaultAddressLine2Placeholder(country),
  cityPlaceholder = getDefaultCityPlaceholder(country),
  statePlaceholder = getDefaultStatePlaceholder(country),
  postalCodePlaceholder = getDefaultPostalCodePlaceholder(country),
  countryOptions,
  countryLabel = "Country",
  countryPlaceholder = "Select Country",
  className,
  ...htmlProps
}: Readonly<AddressFieldProps<TFieldValues, TName>>) {
  const baseFieldName = name as string;
  const countryController = useController({
    control,
    name: `${baseFieldName}.country` as FieldPath<TFieldValues>,
    defaultValue: toCountryFieldValue(country) as never,
  });

  return (
    <div className={cn("space-y-4", className)} {...htmlProps}>
      <div>
        <h3 className="flex items-center gap-1 text-sm leading-snug font-medium">
          {label}
          {required ? (
            <>
              <span className="ms-1 text-red-500" aria-hidden="true">
                *
              </span>
              <span className="sr-only">({requiredLabel})</span>
            </>
          ) : null}
          {description && descriptionAsTooltip && (
            <FormTooltip description={description} />
          )}
        </h3>
        {description && !descriptionAsTooltip && (
          <FormDescription>{description}</FormDescription>
        )}
      </div>

      {/* Address Line 1 and 2 Row */}
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <FormField
          control={control}
          name={`${baseFieldName}.line1` as FieldPath<TFieldValues>}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-block leading-snug">
                {addressLine1Label}
                {required ? (
                  <>
                    <span className="ms-1 text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">({requiredLabel})</span>
                  </>
                ) : null}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  aria-required={required ? "true" : "false"}
                  placeholder={addressLine1Placeholder}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Address Line 2 */}
        {showAddressLine2 ? (
          <FormField
            control={control}
            name={`${baseFieldName}.line2` as FieldPath<TFieldValues>}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="inline-block leading-snug">
                  {addressLine2Label}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    aria-required="false"
                    placeholder={addressLine2Placeholder}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
      </div>

      {/* City and State Row */}
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <FormField
          control={control}
          name={`${baseFieldName}.city` as FieldPath<TFieldValues>}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-block leading-snug">
                {cityLabel}
                {required ? (
                  <>
                    <span className="ms-1 text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">({requiredLabel})</span>
                  </>
                ) : null}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  aria-required={required ? "true" : "false"}
                  placeholder={cityPlaceholder}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* State/Province/County */}
        <FormField
          control={control}
          name={`${baseFieldName}.state` as FieldPath<TFieldValues>}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-block leading-snug">
                {stateLabel}
                {required ? (
                  <>
                    <span className="ms-1 text-red-500" aria-hidden="true">
                      *
                    </span>
                    <span className="sr-only">({requiredLabel})</span>
                  </>
                ) : null}
              </FormLabel>
              {stateOptions && stateOptions.length > 0 ? (
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={statePlaceholder} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {stateOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <FormControl>
                  <Input
                    type="text"
                    aria-required={required ? "true" : "false"}
                    placeholder={statePlaceholder}
                    {...field}
                  />
                </FormControl>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Postal Code */}
      <FormField
        control={control}
        name={`${baseFieldName}.postalCode` as FieldPath<TFieldValues>}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="inline-block leading-snug">
              {postalCodeLabel}
              {required ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            <FormControl>
              <Input
                type="text"
                aria-required={required ? "true" : "false"}
                placeholder={postalCodePlaceholder}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Country */}
      <FormField
        control={control}
        name={`${baseFieldName}.country` as FieldPath<TFieldValues>}
        controllerResult={countryController}
        render={({ field }) => (
          <FormItem>
            <FormLabel className="inline-block leading-snug">
              {countryLabel}
              {required ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            {countryOptions && countryOptions.length > 0 ? (
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={countryPlaceholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {countryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <FormControl>
                <Input
                  type="text"
                  aria-required={required ? "true" : "false"}
                  placeholder={countryPlaceholder}
                  {...field}
                />
              </FormControl>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function getDefaultStateLabel(country: string): string {
  switch (country) {
    case "US":
      return "State";
    case "CA":
      return "Province";
    case "GB":
      return "County";
    case "AU":
      return "State";
    default:
      return "State / Province / Region";
  }
}

function getDefaultPostalCodeLabel(country: string): string {
  switch (country) {
    case "US":
      return "ZIP Code";
    case "CA":
    case "GB":
    case "AU":
      return "Postal Code";
    default:
      return "ZIP / Postal Code";
  }
}

function getDefaultPostalCodePlaceholder(country: string): string {
  switch (country) {
    case "US":
      return "12345";
    case "CA":
      return "A1A 1A1";
    case "GB":
      return "SW1A 1AA";
    case "AU":
      return "2000";
    default:
      return "";
  }
}

function getDefaultAddressLine1Placeholder(country: string): string {
  switch (country) {
    case "US":
      return "123 Main Street";
    case "CA":
      return "123 Main Street";
    case "GB":
      return "123 High Street";
    case "AU":
      return "123 Main Street";
    default:
      return "";
  }
}

function getDefaultAddressLine2Placeholder(country: string): string {
  switch (country) {
    case "US":
      return "Apt 4B";
    case "CA":
      return "Unit 4B";
    case "GB":
      return "Flat 4B";
    case "AU":
      return "Unit 4B";
    default:
      return "";
  }
}

function getDefaultCityPlaceholder(country: string): string {
  switch (country) {
    case "US":
      return "New York";
    case "CA":
      return "Toronto";
    case "GB":
      return "London";
    case "AU":
      return "Sydney";
    default:
      return "";
  }
}

function getDefaultStatePlaceholder(country: string): string {
  switch (country) {
    case "US":
      return "Select state";
    case "CA":
      return "Select province";
    case "GB":
      return "Greater London";
    case "AU":
      return "Select state";
    default:
      return "";
  }
}
